import { useEffect, useMemo, useState } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { ArrowLeft, Eye, EyeOff, Lock, Mail, Shield, UserCheck } from 'lucide-react'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'

import { useAuth } from '@/context/AuthContext'
import { checkSupabaseAuthReachability } from '@/lib/supabase-auth-errors'
import { normalizeDoctorAccessCode, registerDoctorAccessHintFromLink } from '@/lib/doctor-access'

export default function DoctorAccess() {
  const { user, profile, signIn, signOut, loading } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const prefilledIdentifier = useMemo(() => searchParams.get('id') || '', [searchParams])
  const hasDoctorLinkIntent = useMemo(
    () =>
      Boolean(
        prefilledIdentifier ||
          searchParams.get('patient') ||
          searchParams.get('doctorEmail') ||
          searchParams.get('patientName')
      ),
    [prefilledIdentifier, searchParams]
  )
  const metadataRole = user?.user_metadata?.role
  const effectiveRole =
    profile?.role ||
    (metadataRole === 'doctor' || metadataRole === 'admin' || metadataRole === 'patient'
      ? metadataRole
      : null)
  const [identifier, setIdentifier] = useState(prefilledIdentifier)
  const [code, setCode] = useState('')
  const [showCode, setShowCode] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [isSwitchingSession, setIsSwitchingSession] = useState(false)
  const [authIssue, setAuthIssue] = useState<string | null>(null)

  useEffect(() => {
    const patientId = searchParams.get('patient') || ''
    const patientDisplayName = searchParams.get('patientName') || ''
    const doctorName = searchParams.get('doctorName') || ''
    const doctorEmail = (searchParams.get('doctorEmail') || prefilledIdentifier || '').trim().toLowerCase()

    if (!patientId || !doctorEmail) {
      return
    }

    registerDoctorAccessHintFromLink({
      patientId,
      patientDisplayName: patientDisplayName || 'Patiente',
      doctorName: doctorName || doctorEmail,
      doctorEmail,
      doctorSpecialty: searchParams.get('specialty') || null,
      loginIdentifier: doctorEmail
    })
  }, [prefilledIdentifier, searchParams])

  useEffect(() => {
    let cancelled = false

    async function runReachabilityCheck() {
      const result = await checkSupabaseAuthReachability()

      if (!cancelled) {
        setAuthIssue(result.reachable ? null : result.message)
      }
    }

    void runReachabilityCheck()

    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (!user || !effectiveRole || effectiveRole === 'doctor' || effectiveRole === 'admin' || !hasDoctorLinkIntent) {
      return
    }

    let cancelled = false

    async function switchToDoctorAccess() {
      setIsSwitchingSession(true)

      try {
        await signOut()
      } catch (error) {
        if (!cancelled) {
          const message = error instanceof Error ? error.message : 'Impossible de changer de session'
          toast.error(message)
        }
      } finally {
        if (!cancelled) {
          setIsSwitchingSession(false)
        }
      }
    }

    void switchToDoctorAccess()

    return () => {
      cancelled = true
    }
  }, [effectiveRole, hasDoctorLinkIntent, signOut, user])

  if (loading || isSwitchingSession) {
    return (
      <div className="min-h-screen bg-dark-bg flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="spinner" />
          {isSwitchingSession && (
            <p className="text-sm text-slate-400">
              Basculement vers l acces medecin...
            </p>
          )}
        </div>
      </div>
    )
  }

  if (user && (effectiveRole === 'doctor' || effectiveRole === 'admin')) {
    return <Navigate to="/doctor" replace />
  }

  if (user && effectiveRole && effectiveRole !== 'doctor' && effectiveRole !== 'admin' && !hasDoctorLinkIntent) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setSubmitting(true)

    try {
      await signIn(identifier.trim().toLowerCase(), normalizeDoctorAccessCode(code))
      toast.success('Connexion medecin reussie')
    } catch (error) {
      const message = error instanceof Error ? error.message : "Connexion impossible"
      toast.error(message)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div className="bg-dark-surface border border-dark-border rounded-2xl p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-500 flex items-center justify-center mx-auto mb-4 shadow-glow-sm">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">Acces medecin</h1>
            <p className="text-slate-400 text-sm">
              Connectez-vous avec l'identifiant et le code transmis par la patiente.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {authIssue && (
              <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-4">
                <p className="text-sm font-semibold text-rose-200">Serveur de connexion indisponible</p>
                <p className="mt-2 text-sm text-rose-100/85">{authIssue}</p>
              </div>
            )}

            <div>
              <label htmlFor="doctor-identifier" className="block text-sm font-medium text-slate-300 mb-2">
                Identifiant
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                <input
                  id="doctor-identifier"
                  type="text"
                  value={identifier}
                  onChange={(event) => setIdentifier(event.target.value)}
                  className="w-full pl-12 pr-4 py-3 bg-dark-surface-high border border-dark-border rounded-xl text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                  placeholder="identifiant-medecin"
                  autoComplete="username"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="doctor-code" className="block text-sm font-medium text-slate-300 mb-2">
                Code d'acces
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-3.5 w-5 h-5 text-slate-400" />
                <input
                  id="doctor-code"
                  type={showCode ? 'text' : 'password'}
                  value={code}
                  onChange={(event) => setCode(normalizeDoctorAccessCode(event.target.value))}
                  className="w-full pl-12 pr-12 py-3 bg-dark-surface-high border border-dark-border rounded-xl text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 transition-colors"
                  placeholder="Code fourni par la patiente"
                  autoComplete="current-password"
                  autoCapitalize="characters"
                  autoCorrect="off"
                  spellCheck={false}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowCode((current) => !current)}
                  className="absolute right-3 top-3.5 text-slate-400 hover:text-white transition-colors"
                  aria-label={showCode ? 'Masquer le code' : 'Afficher le code'}
                >
                  {showCode ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting || !identifier.trim() || !code.trim()}
              className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 disabled:from-slate-700 disabled:to-slate-600 disabled:cursor-not-allowed text-white py-3 px-6 rounded-xl font-semibold transition-all duration-200 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <div className="spinner w-5 h-5" />
              ) : (
                <>
                  <UserCheck className="w-5 h-5" />
                  Ouvrir l'interface medecin
                </>
              )}
            </button>
          </form>

          <div className="mt-8 rounded-xl border border-blue-800/40 bg-blue-900/20 p-4">
            <h2 className="text-sm font-semibold text-blue-200">Acces reserve</h2>
            <p className="mt-2 text-xs text-blue-100/80">
              Seuls les modules utiles au suivi medical sont affiches cote praticien.
            </p>
          </div>

          <div className="mt-6 text-center">
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm"
              type="button"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour au suivi patient
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  )
}
